/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Monitor and troubleshoot journeys", description = "Track live journey performance, inspect member paths, manage an active journey, and diagnose entry, timing, exit, and delivery issues.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Monitor and troubleshoot journeys",
        description: "Track live journey performance, inspect member paths, manage an active journey, and diagnose entry, timing, exit, and delivery issues.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Monitor journey performance",
          "depth": 0,
          "id": "monitor-journey-performance"
        }, {
          "value": "Inspect a member's path",
          "depth": 0,
          "id": "inspect-a-members-path"
        }, {
          "value": "Journey logs",
          "depth": 0,
          "id": "journey-logs"
        }, {
          "value": "Manage a live journey",
          "depth": 0,
          "id": "manage-a-live-journey"
        }, {
          "value": "Edit a journey",
          "depth": 1,
          "id": "edit-a-journey"
        }, {
          "value": "Pause and resume",
          "depth": 1,
          "id": "pause-and-resume"
        }, {
          "value": "Turn off a journey",
          "depth": 1,
          "id": "turn-off-a-journey"
        }, {
          "value": "Trigger a manual run",
          "depth": 1,
          "id": "trigger-a-manual-run"
        }, {
          "value": "Reset a journey",
          "depth": 1,
          "id": "reset-a-journey"
        }, {
          "value": "Configure alerts",
          "depth": 0,
          "id": "configure-alerts"
        }, {
          "value": "Troubleshoot a journey",
          "depth": 0,
          "id": "troubleshoot-a-journey"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      ol: "ol",
      code: "code",
      h3: "h3",
      h4: "h4",
      hr: "hr"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and operations teams running live journeys"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["An activated journey (see ", _jsx(_components.a, {
                href: "/customer-studio/journeys/activate",
                children: "Test and launch a journey"
              }), ")"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Monitor a live journey from the canvas and performance panel. Use member paths and run logs to investigate unexpected behavior, then pause, edit, or turn off the journey when needed."
        })
      }), "\n", _jsx(_components.h2, {
        id: "monitor-journey-performance",
        children: "Monitor journey performance"
      }), "\n", _jsx(_components.p, {
        children: "The journey canvas shows how members move through each tile. Line thickness reflects volume. Hover over a line to see counts."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-results-visualization.png",
          alt: "Viewing the journey results on the canvas"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Performance"
        }), " panel shows metrics across the journey's history unless you reset it:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "In Progress"
          }), " — Members currently waiting in a tile."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Entered"
          }), " — Members who have entered the journey."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Met Exit Criteria"
          }), " — Members removed by global exit criteria."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Finished"
          }), " — Members who reached the end."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-performance-tile.png",
          alt: "Viewing the journey performance tile in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Select a tile to see tile-specific counts. Segment, A/B Split, and Hold until tiles also show per-branch counts and percentages."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-path-breakdown.png",
          alt: "Viewing the journey performance path breakdown"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["These counts are cumulative across every run since the journey started, not a point-in-time snapshot, and they count re-entries. Entered, advanced, and exit totals are tallied independently, so they don't form a strict funnel and won't always add up. ", _jsx(_components.strong, {
          children: "In Progress"
        }), " is the exception — it's the live count of members waiting in a tile right now."]
      }), "\n", _jsx(_components.h2, {
        id: "inspect-a-members-path",
        children: "Inspect a member's path"
      }), "\n", _jsx(_components.p, {
        children: "Use the member path to understand why a specific member entered, exited, or followed a branch."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select a count in the ", _jsx(_components.strong, {
            children: "Performance"
          }), " panel to open the member list. The list shows a sample of up to 100 members, and you can search by primary key."]
        }), "\n", _jsx(_components.li, {
          children: "Select a member to view their attributes and step-by-step path through the journey."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-performance-member-detail.png",
          alt: "Viewing the member details from journey performance tile in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/journey-individual-path.png",
          alt: "Viewing an individual member's path through a journey"
        })
      }), "\n", _jsx(_components.h2, {
        id: "journey-logs",
        children: "Journey logs"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch writes journey data to tables in your warehouse under the ", _jsx(_components.code, {
          children: "HIGHTOUCH_PLANNER"
        }), " schema for reporting, debugging, and custom analysis. Workspace-level read-only views in ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " aggregate data across journeys."]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/developer-tools/warehouse-tables#journey-tables",
          children: "Journey tables"
        }), " and ", _jsx(_components.a, {
          href: "/developer-tools/warehouse-tables#journey-views",
          children: "Journey views"
        }), " for schema details."]
      }), "\n", _jsx(_components.h2, {
        id: "manage-a-live-journey",
        children: "Manage a live journey"
      }), "\n", _jsx(_components.h3, {
        id: "edit-a-journey",
        children: "Edit a journey"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Edit"
        }), " on the canvas to update the journey."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-edit-mode.png",
          alt: "Journey canvas showing the Edit mode banner and edit controls"
        })
      }), "\n", _jsx(_components.p, {
        children: "In edit mode, you can update the journey name, description, evaluation schedule, exit criteria, variables, alerting, and journey tiles."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-edit-settings.png",
          alt: "Journey settings panel in edit mode"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Editing a live journey affects members already in progress. Members continue\nfrom their current position, but changes to later tiles can change what\nhappens next. For major changes, create a replacement journey and drain the\nexisting one instead of editing it in place."
        })
      }), "\n", _jsx(_components.h3, {
        id: "pause-and-resume",
        children: "Pause and resume"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Pause"
        }), " to stop evaluations and sync triggering. While paused, members don't enter, advance, exit, or trigger destination actions."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-pause.png",
          alt: "Pause and turn off button"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Resume"
        }), " to continue evaluation from each member's current position."]
      }), "\n", _jsx(_components.h3, {
        id: "turn-off-a-journey",
        children: "Turn off a journey"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Turn off"
        }), " to stop the journey permanently. Choose how to handle members already in progress:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Existing members should complete the journey first"
          }), " ", _jsx(_components.em, {
            children: "(default)"
          }), " — The journey enters ", _jsx(_components.strong, {
            children: "Draining"
          }), ". No new members enter, but existing members continue until they finish or exit. The journey becomes ", _jsx(_components.strong, {
            children: "Off"
          }), " when no members remain."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Hard stop"
          }), " — All members are removed on the next evaluation run."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journey-turn-off.png",
          alt: "Turn off journey modal"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Draining"
        }), " when retiring or replacing a journey without interrupting members already in progress."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-draining.png",
          alt: "Draining status chip on a journey"
        })
      }), "\n", _jsx(_components.h3, {
        id: "trigger-a-manual-run",
        children: "Trigger a manual run"
      }), "\n", _jsxs(_components.p, {
        children: ["For active journeys, select ", _jsx(_components.strong, {
          children: "Trigger manual run"
        }), " from the actions menu to run an evaluation outside the regular schedule."]
      }), "\n", _jsx(_components.p, {
        children: "Use a manual run when you need Hightouch to evaluate members after a data refresh or configuration change without waiting for the next scheduled run."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-actions-menu.png",
          alt: "The journey actions menu with Trigger manual run, Make a copy, Create template from this journey, and View history"
        })
      }), "\n", _jsx(_components.h3, {
        id: "reset-a-journey",
        children: "Reset a journey"
      }), "\n", _jsx(_components.p, {
        children: "For any journey that isn't currently active, reset it to clear member progress, run history, and journey log data."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Resetting a journey permanently deletes member progress, run history, and\njourney log table contents. This can't be undone."
        })
      }), "\n", _jsx(_components.h2, {
        id: "configure-alerts",
        children: "Configure alerts"
      }), "\n", _jsx(_components.p, {
        children: "Journey alerts notify you when a run fails. Journeys inherit workspace alert settings, which you can override at the journey level."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-settings-alerting.png",
          alt: "Journey alerting settings"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recipients"
          }), " — Send alerts by email, Slack, PagerDuty, SMS, webhook, or another configured channel."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Triggers"
          }), " — Journeys currently support alerts for fatal errors in a journey run, not for failures in the syncs a journey triggers. If no trigger is configured, failures appear in the Hightouch UI only."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Alerts fire on a status change, not on every run: a journey that starts failing sends one alert, further consecutive failures don't re-alert, and the next successful run sends one recovery alert."
      }), "\n", _jsxs(_components.p, {
        children: ["Configure workspace defaults under ", _jsx(_components.strong, {
          children: "Alerting > Journeys"
        }), ". See ", _jsx(_components.a, {
          href: "/syncs/alerting#how-alerting-works",
          children: "Sync alerting"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "troubleshoot-a-journey",
        children: "Troubleshoot a journey"
      }), "\n", _jsx(_components.p, {
        children: "When a journey doesn't behave as expected, start with three checks:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Confirm when the journey last ran and how often it runs."
        }), "\n", _jsx(_components.li, {
          children: "Confirm the relevant warehouse data was available at that run."
        }), "\n", _jsx(_components.li, {
          children: "Inspect the member path or run log for the point where behavior diverged from expectations."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For warehouse SQL errors such as invalid identifiers, unresolved columns, or missing objects, see ", _jsx(_components.a, {
          href: "/models/sql-compilation-errors",
          children: "Resolve SQL compilation errors"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "members-arent-entering",
        children: "Members aren't entering"
      }), "\n", _jsx(_components.p, {
        children: "Entry is checked on each journey run. A member won't enter if the entry condition isn't true at run time, the data hasn't refreshed, or entry settings prevent them from qualifying."
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Start"
          }), " tile entry source, filters, exclusions, and re-entry settings."]
        }), "\n", _jsx(_components.li, {
          children: "For event entry, the event model primary key, selected event, and lookback window."
        }), "\n", _jsx(_components.li, {
          children: "The journey's last run time and evaluation schedule."
        }), "\n", _jsx(_components.li, {
          children: "The entry audience preview."
        }), "\n", _jsx(_components.li, {
          children: "Whether the member entered and immediately met exit criteria."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "delays-or-hold-until-tiles-take-longer-than-expected",
        children: "Delays or Hold until tiles take longer than expected"
      }), "\n", _jsx(_components.p, {
        children: "Delays and hold conditions are evaluated on journey runs, not continuously. A member advances on the first run after the delay or condition is satisfied."
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The delay duration against the journey's evaluation interval."
        }), "\n", _jsx(_components.li, {
          children: "Whether multiple delay or hold tiles add additional runs to the total wait."
        }), "\n", _jsx(_components.li, {
          children: "Whether the journey needs a more frequent schedule."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/customer-studio/journeys#how-the-schedule-affects-timing",
          children: "How the schedule affects timing"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "members-arent-exiting",
        children: "Members aren't exiting"
      }), "\n", _jsx(_components.p, {
        children: "Exit criteria are checked on journey runs. If the condition becomes true between runs, the member remains until the next run detects it."
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The action timestamp against the journey's run history."
        }), "\n", _jsx(_components.li, {
          children: "The exit criteria in journey settings."
        }), "\n", _jsx(_components.li, {
          children: "Whether the warehouse contains the updated data."
        }), "\n", _jsx(_components.li, {
          children: "The member path to see when the member exited and which condition matched."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "members-are-re-entering-unexpectedly",
        children: "Members are re-entering unexpectedly"
      }), "\n", _jsx(_components.p, {
        children: "Re-entry settings determine how many times a member can enter and how soon they can return."
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Start"
          }), " tile re-entry and simultaneous-entry settings."]
        }), "\n", _jsx(_components.li, {
          children: "For event entry, whether repeated qualifying events are expected."
        }), "\n", _jsx(_components.li, {
          children: "The re-entry delay."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "a-destination-tile-shows-members-but-syncs-zero-records",
        children: "A destination tile shows members but syncs zero records"
      }), "\n", _jsxs(_components.p, {
        children: ["If members reached ", _jsx(_components.strong, {
          children: "Send to destination"
        }), " but the sync processed zero records, check the sync configuration rather than journey entry logic."]
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Whether the sync is paused."
        }), "\n", _jsx(_components.li, {
          children: "Whether the sync's model is linked to the journey output."
        }), "\n", _jsx(_components.li, {
          children: "Liquid templates in the sync configuration."
        }), "\n", _jsx(_components.li, {
          children: "Whether the members reached the tile during a completed live run rather than a journey test."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "journey-and-destination-counts-dont-match",
        children: "Journey and destination counts don't match"
      }), "\n", _jsxs(_components.p, {
        children: ["A journey can process more members than a destination receives, because filtering happens at several stages after journey qualification. Check the stages in order and stop where the count drops. See ", _jsx(_components.a, {
          href: "/customer-studio/journeys#how-journey-membership-works",
          children: "How journey membership works"
        }), " for the underlying model."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/counts-diagnostic.svg",
          alt: "A diagnostic checklist for when journey and destination counts differ: check Audience, Priority list, Journey logic, Sync run, Destination rules, Consent, and Destination platform in order, and stop where the count drops"
        })
      }), "\n", _jsx(_components.p, {
        children: "Most drops happen on the destination side. Where the count fell, check:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Priority list"
          }), " — audience ranking and overlapping membership, in case a higher-priority audience claimed the member."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Destination rules"
          }), " — the ", _jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "rules"
          }), " applied to the sync, and any ", _jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "subset"
          }), " scoping the audience upstream."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Consent"
          }), " — consent status in the warehouse or OneTrust."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Destination platform"
          }), " — the destination's delivery logs for suppression or deduplication."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Also check for a ", _jsx(_components.code, {
          children: "Users might never be synced"
        }), " warning on the destination tile. If a cohort sync removes members on exit and the destination tile is the final tile, add a ", _jsx(_components.strong, {
          children: "Time delay"
        }), " tile after it so members remain in the cohort for at least one run."]
      }), "\n", _jsx(_components.h4, {
        id: "a-journey-run-fails",
        children: "A journey run fails"
      }), "\n", _jsxs(_components.p, {
        children: ["Open the ", _jsx(_components.strong, {
          children: "Run Log"
        }), " tab to find the failed run and its error message."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/journeys/cs-journeys-run-log.png",
          alt: "The Run Log tab showing each journey run's start time, status, and run ID"
        })
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "The error for the failed run."
        }), "\n", _jsxs(_components.li, {
          children: ["For SQL-related errors, ", _jsx(_components.a, {
            href: "/models/sql-compilation-errors",
            children: "Resolve SQL compilation errors"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["If the ", _jsx(_components.strong, {
            children: "Start"
          }), " tile's error says ", _jsx(_components.code, {
            children: "could not find subsets for this parent model"
          }), ", a subset selected on that tile was deleted. Remove it from the Start tile. See ", _jsx(_components.a, {
            href: "/customer-studio/journeys/create#subsets",
            children: "Subsets"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Warehouse credentials and permissions for the parent model's underlying tables."
        }), "\n", _jsx(_components.li, {
          children: "Warehouse concurrency or query timeout limits if the failure is intermittent."
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#configure-alerts",
            children: "Journey alerting"
          }), " so Hightouch notifies you when future runs fail."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "members-receive-duplicate-sends",
        children: "Members receive duplicate sends"
      }), "\n", _jsx(_components.p, {
        children: "Duplicate sends usually come from sync mode, re-entry behavior, or an unstable primary key."
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The sync mode on ", _jsx(_components.strong, {
            children: "Send to destination"
          }), ". For one-time actions, use ", _jsx(_components.a, {
            href: "/customer-studio/journeys/tiles#message-mode",
            children: "Message mode"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Start"
          }), " tile re-entry settings."]
        }), "\n", _jsx(_components.li, {
          children: "For cohort syncs, the configured removal behavior."
        }), "\n", _jsx(_components.li, {
          children: "The member path to see whether the member reached the tile once or multiple times."
        }), "\n", _jsx(_components.li, {
          children: "That the parent model's primary key is stable and unique. A key that changes — for example, when identity resolution reassigns it — can re-fire earlier sends."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "members-follow-an-unexpected-branch",
        children: "Members follow an unexpected branch"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Segment"
        }), " evaluates branches from top to bottom and uses the first matching branch."]
      }), "\n", _jsx(_components.p, {
        children: "Check:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Branch order."
        }), "\n", _jsx(_components.li, {
          children: "Whether branch conditions overlap."
        }), "\n", _jsx(_components.li, {
          children: "The member path to see which branch matched."
        }), "\n", _jsx(_components.li, {
          children: "Warehouse data freshness if live behavior differs from a journey test."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "test-results-differ-from-live-runs",
        children: "Test results differ from live runs"
      }), "\n", _jsx(_components.p, {
        children: "Journey tests evaluate conditions in a single pass with delays skipped. Live runs use real timing, current warehouse data, exit evaluation, cross-journey interactions, and destination behavior."
      }), "\n", _jsx(_components.p, {
        children: "Use:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "A journey test to validate branches and conditions."
        }), "\n", _jsx(_components.li, {
          children: "Test syncs to validate field mappings and payload formatting."
        }), "\n", _jsx(_components.li, {
          children: "A small live cohort to validate timing, exits, and end-to-end delivery."
        }), "\n", _jsx(_components.li, {
          children: "Run history and data freshness when comparing test and live results."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/priority-lists",
            children: "Priority lists"
          }), " when audience priority affects who can enter a journey."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/developer-tools/warehouse-tables#journey-tables",
            children: "Warehouse tables reference"
          }), " for journey log schemas."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
